import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader, Empty, Stat, Change } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bank, LinkSimpleBreak, ShieldWarning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrokerAnalytics from "@/components/BrokerAnalytics";
import { AssetLogo } from "@/components/AssetLogo";

function LinkCard({ onLinked }) {
  const [form, setForm] = useState({ api_key: "", secret_key: "", paper: true, live_confirmed: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const link = async () => {
    if (!form.api_key.trim() || !form.secret_key.trim()) { toast.error("Enter your Alpaca API key and secret"); return; }
    if (!form.paper && !form.live_confirmed) { toast.error("Confirm you understand LIVE orders use real money"); return; }
    setBusy(true);
    try {
      await api.post("/alpaca/link", form);
      toast.success(`Alpaca ${form.paper ? "paper" : "LIVE"} account linked`);
      onLinked();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-6 max-w-lg" data-testid="alpaca-link-card">
      <div className="flex items-center gap-2"><Bank size={20} className="text-volt" weight="fill" /><h3 className="font-heading text-lg font-semibold">Link your Alpaca account</h3></div>
      <p className="mt-2 text-sm text-muted-foreground">Route orders to a real brokerage. Keys are validated, encrypted, and stored on our server — never in your browser.</p>

      <div className="mt-5 flex gap-2" data-testid="alpaca-env-toggle">
        <button data-testid="env-paper" onClick={() => set("paper", true)} className={`flex-1 rounded-sm border px-3 py-2 text-sm transition-colors ${form.paper ? "border-signal-green bg-signal-green/10 text-signal-green" : "border-border text-muted-foreground"}`}>Paper (paper-api)</button>
        <button data-testid="env-live" onClick={() => set("paper", false)} className={`flex-1 rounded-sm border px-3 py-2 text-sm transition-colors ${!form.paper ? "border-signal-red bg-signal-red/10 text-signal-red" : "border-border text-muted-foreground"}`}>Live (real money)</button>
      </div>

      <div className="mt-4 space-y-3">
        <div><div className="label-xs mb-1.5">API Key ID</div><Input data-testid="alpaca-key-input" value={form.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder="PK..." className="bg-background rounded-sm mono" /></div>
        <div><div className="label-xs mb-1.5">Secret Key</div><Input data-testid="alpaca-secret-input" type="password" value={form.secret_key} onChange={(e) => set("secret_key", e.target.value)} placeholder="••••••••" className="bg-background rounded-sm mono" /></div>
      </div>

      {!form.paper && (
        <label data-testid="alpaca-live-confirm" className="mt-4 flex items-start gap-2 border border-signal-red/40 bg-signal-red/5 p-3 rounded-sm cursor-pointer">
          <input type="checkbox" checked={form.live_confirmed} onChange={(e) => set("live_confirmed", e.target.checked)} className="mt-0.5" />
          <span className="text-xs text-signal-red flex items-start gap-1.5"><ShieldWarning size={16} className="shrink-0 mt-px" /> I understand LIVE orders execute with real money in my Alpaca account.</span>
        </label>
      )}

      <Button data-testid="alpaca-link-btn" onClick={link} disabled={busy} className="mt-5 w-full bg-volt hover:bg-volt/90 text-white rounded-sm">{busy ? "Validating…" : "Validate & Link"}</Button>
    </div>
  );
}

function OrderForm({ paper, onPlaced }) {
  const [o, setO] = useState({ ticker: "AAPL", side: "buy", quantity: 1, order_type: "market", limit_price: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setO((x) => ({ ...x, [k]: v }));

  const submit = async () => {
    if (!o.ticker.trim() || Number(o.quantity) <= 0) { toast.error("Enter a valid ticker and quantity"); return; }
    if (o.order_type === "limit" && !(Number(o.limit_price) > 0)) { toast.error("Enter a limit price"); return; }
    if (!paper && !window.confirm(`Place a REAL ${o.side.toUpperCase()} order for ${o.quantity} ${o.ticker.toUpperCase()}? This uses real money.`)) return;
    setBusy(true);
    try {
      const payload = { ticker: o.ticker, side: o.side, quantity: Number(o.quantity), order_type: o.order_type, live_confirmed: !paper };
      if (o.order_type === "limit") payload.limit_price = Number(o.limit_price);
      const r = await api.post("/alpaca/orders", payload);
      toast.success(`Order ${r.data.order.status || "submitted"} · ${o.ticker.toUpperCase()}`);
      onPlaced();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-5" data-testid="alpaca-order-form">
      <div className="label-xs mb-3">Place Broker Order · {paper ? "Paper" : "LIVE"}</div>
      <div className="space-y-3">
        <div><div className="label-xs mb-1.5">Symbol</div><Input data-testid="broker-ticker" value={o.ticker} onChange={(e) => set("ticker", e.target.value.toUpperCase())} className="bg-background rounded-sm mono" /><div className="mono text-[10px] text-muted-foreground mt-1">Stocks (AAPL) or crypto pairs (BTC/USD, ETH/USD)</div></div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="broker-side-buy" onClick={() => set("side", "buy")} className={`rounded-sm border px-3 py-2 text-sm ${o.side === "buy" ? "border-signal-green bg-signal-green/10 text-signal-green" : "border-border text-muted-foreground"}`}>Buy</button>
          <button data-testid="broker-side-sell" onClick={() => set("side", "sell")} className={`rounded-sm border px-3 py-2 text-sm ${o.side === "sell" ? "border-signal-red bg-signal-red/10 text-signal-red" : "border-border text-muted-foreground"}`}>Sell</button>
        </div>
        <div><div className="label-xs mb-1.5">Quantity</div><Input data-testid="broker-qty" type="number" min="0.0001" value={o.quantity} onChange={(e) => set("quantity", e.target.value)} className="bg-background rounded-sm mono" /></div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="broker-type-market" onClick={() => set("order_type", "market")} className={`rounded-sm border px-3 py-2 text-sm ${o.order_type === "market" ? "border-volt bg-volt/10 text-volt" : "border-border text-muted-foreground"}`}>Market</button>
          <button data-testid="broker-type-limit" onClick={() => set("order_type", "limit")} className={`rounded-sm border px-3 py-2 text-sm ${o.order_type === "limit" ? "border-volt bg-volt/10 text-volt" : "border-border text-muted-foreground"}`}>Limit</button>
        </div>
        {o.order_type === "limit" && <div><div className="label-xs mb-1.5">Limit Price</div><Input data-testid="broker-limit" type="number" value={o.limit_price} onChange={(e) => set("limit_price", e.target.value)} className="bg-background rounded-sm mono" /></div>}
        <Button data-testid="broker-submit" onClick={submit} disabled={busy} className={`w-full rounded-sm text-black ${paper ? "bg-signal-green hover:bg-signal-green/90" : "bg-signal-red hover:bg-signal-red/90 text-white"}`}>{busy ? "Submitting…" : `${paper ? "Submit Paper" : "Submit LIVE"} Order`}</Button>
      </div>
    </div>
  );
}

export default function AlpacaBroker({ onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [acctError, setAcctError] = useState(null);

  const loadStatus = useCallback(() => api.get("/alpaca/status").then((r) => r.data).catch(() => ({ linked: false })), []);

  // Track order statuses to pop a toast the moment an order fills.
  const prevOrders = useRef(null);
  const applyOrders = useCallback((list) => {
    const prev = prevOrders.current;
    if (prev) {
      list.forEach((o) => {
        const before = prev[o.id];
        const filledNow = o.status === "filled" || o.status === "partially_filled";
        const wasFilled = before === "filled" || before === "partially_filled";
        if (before && before !== o.status && filledNow && !wasFilled) {
          toast.success(`Order filled · ${(o.side || "").toUpperCase()} ${o.filled_qty || o.quantity} ${o.ticker}`,
            { description: o.filled_avg_price ? `@ $${o.filled_avg_price}` : undefined });
        }
      });
    }
    prevOrders.current = Object.fromEntries(list.map((o) => [o.id, o.status]));
    setOrders(list);
  }, []);

  const loadAll = useCallback(async () => {
    const st = await loadStatus();
    setStatus(st);
    if (onStatusChange) onStatusChange(st);
    if (st.linked) {
      api.get("/alpaca/account")
        .then((r) => { setAccount(r.data); setAcctError(null); })
        .catch((e) => { setAccount(null); setAcctError(formatApiErrorDetail(e.response?.data?.detail)); });
      api.get("/alpaca/positions").then((r) => setPositions(r.data.positions)).catch(() => setPositions([]));
      api.get("/alpaca/orders").then((r) => applyOrders(r.data.orders)).catch(() => setOrders([]));
    }
  }, [loadStatus, applyOrders, onStatusChange]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh: poll account/positions/orders while linked so accepted → filled
  // status updates (and fill toasts) appear without a manual reload.
  useEffect(() => {
    if (!status?.linked) return;
    const id = setInterval(() => {
      api.get("/alpaca/account").then((r) => setAccount(r.data)).catch(() => {});
      api.get("/alpaca/positions").then((r) => setPositions(r.data.positions)).catch(() => {});
      api.get("/alpaca/orders").then((r) => applyOrders(r.data.orders)).catch(() => {});
    }, 12000);
    return () => clearInterval(id);
  }, [status?.linked, applyOrders]);

  const unlink = async () => {
    if (!window.confirm("Unlink your Alpaca account? Your keys will be deleted from our server.")) return;
    try { await api.post("/alpaca/unlink"); toast.success("Alpaca account unlinked"); setAccount(null); setPositions([]); setOrders([]); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const switchEnv = async (env) => {
    if (env === status.active_env) return;
    if (env === "live" && !window.confirm("Switch to your LIVE account? Orders will use REAL money.")) return;
    try {
      await api.post("/alpaca/switch", { env });
      toast.success(`Switched to ${env === "paper" ? "Paper" : "Live"} account`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/alpaca/orders/${id}`); toast.success("Cancel requested"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const closePos = async (p) => {
    if (!window.confirm(`Close your ${p.quantity} ${p.ticker} position on Alpaca${status.paper ? " (paper)" : " — REAL MONEY"}?`)) return;
    try {
      await api.post("/alpaca/positions/close", { ticker: p.ticker, live_confirmed: !status.paper });
      toast.success(`Closing ${p.ticker}`); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const closeAll = async () => {
    if (!window.confirm(`Close ALL ${positions.length} broker position(s) on Alpaca${status.paper ? " (paper)" : " — REAL MONEY"}? This also cancels open orders.`)) return;
    try {
      const r = await api.post("/alpaca/positions/close_all", { live_confirmed: !status.paper });
      toast.success(`Closing ${r.data.count} position(s)`); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!status) return <Loader label="Loading broker" />;
  if (!status.linked) return <LinkCard onLinked={loadAll} />;

  return (
    <div data-testid="alpaca-broker">
      <div className="flex items-center justify-between border border-border bg-card px-4 py-3">
        <div className="flex items-center gap-2">
          <CheckCircle size={18} weight="fill" className="text-signal-green" />
          <span className="text-sm">Linked to Alpaca <b className="mono">{status.account_id?.slice(0, 8)}…</b></span>
          {(status.envs_linked?.paper && status.envs_linked?.live) ? (
            <div className="flex items-center rounded-sm overflow-hidden border border-border" data-testid="alpaca-env-switch">
              <button data-testid="switch-env-paper" onClick={() => switchEnv("paper")} className={`mono text-[11px] px-2.5 py-1 transition-colors duration-150 ${status.paper ? "bg-signal-green/20 text-signal-green" : "text-muted-foreground hover:text-white"}`}>PAPER</button>
              <button data-testid="switch-env-live" onClick={() => switchEnv("live")} className={`mono text-[11px] px-2.5 py-1 transition-colors duration-150 ${!status.paper ? "bg-signal-red/20 text-signal-red" : "text-muted-foreground hover:text-white"}`}>LIVE</button>
            </div>
          ) : (
            <span className={`mono text-[11px] px-2 py-0.5 rounded-sm ${status.paper ? "bg-signal-green/15 text-signal-green" : "bg-signal-red/15 text-signal-red"}`}>{status.paper ? "PAPER" : "LIVE · REAL MONEY"}</span>
          )}
        </div>
        <Button data-testid="alpaca-unlink-btn" onClick={unlink} variant="outline" size="sm" className="rounded-sm border-border gap-1.5 h-8"><LinkSimpleBreak size={14} /> Unlink</Button>
      </div>

      {acctError && (
        <div data-testid="alpaca-account-error" className="mt-4 flex items-start justify-between gap-3 border border-signal-red/40 bg-signal-red/[0.06] p-4 rounded-sm">
          <div className="flex items-start gap-2">
            <ShieldWarning size={18} weight="fill" className="text-signal-red shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-signal-red">Broker connection problem</div>
              <p className="text-xs text-muted-foreground mt-0.5">{acctError}</p>
            </div>
          </div>
          <Button data-testid="alpaca-relink-btn" onClick={unlink} size="sm" className="shrink-0 bg-volt hover:bg-volt/90 text-white rounded-sm h-8 gap-1.5"><LinkSimpleBreak size={14} /> Re-link</Button>
        </div>
      )}

      {account && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Equity" value={`$${account.equity.toLocaleString()}`} />
          <Stat label="Buying Power" value={`$${account.buying_power.toLocaleString()}`} />
          <Stat label="Cash" value={`$${account.cash.toLocaleString()}`} />
          <Stat label="Status" value={<span className={`uppercase ${account.trading_blocked ? "text-signal-red" : "text-signal-green"}`}>{account.trading_blocked ? "Blocked" : account.status}</span>} />
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card">
          <div className="border-b border-border px-4 py-3 label-xs flex items-center justify-between">
            <span>Broker Positions</span>
            {positions.length > 0 && (
              <Button data-testid="close-all-broker-btn" size="sm" variant="outline" onClick={closeAll}
                className="h-7 rounded-sm border-signal-red/50 text-signal-red hover:bg-signal-red/10 text-[11px] gap-1">
                <ShieldWarning size={13} weight="fill" /> Close All
              </Button>
            )}
          </div>
          {positions.length === 0 ? <Empty>No open positions in your Alpaca account</Empty> : (
            <>
              <div className="grid grid-cols-[1fr_60px_80px_80px_1fr_70px] gap-2 px-4 py-2 label-xs border-b border-border/50"><span>Symbol</span><span className="text-right">Qty</span><span className="text-right">Avg</span><span className="text-right">Last</span><span className="text-right">Unreal. P/L</span><span className="text-right">Action</span></div>
              {positions.map((p) => (
                <div key={p.ticker} className="grid grid-cols-[1fr_60px_80px_80px_1fr_70px] gap-2 items-center px-4 py-3 border-b border-border/50">
                  <div className="mono font-medium flex items-center gap-2"><AssetLogo ticker={p.ticker} size={22} />{p.ticker}</div>
                  <div className="mono text-right text-sm">{p.quantity}</div>
                  <div className="mono text-right text-sm">${p.avg_price}</div>
                  <div className="mono text-right text-sm">${p.current_price}</div>
                  <div className="text-right"><div className={`mono text-sm ${p.unrealized_pnl >= 0 ? "pos" : "neg"}`}>{p.unrealized_pnl >= 0 ? "+" : ""}${p.unrealized_pnl.toFixed(2)}</div><div className="mono text-[11px]"><Change value={p.unrealized_pnl_pct} /></div></div>
                  <div className="text-right"><Button data-testid={`close-broker-${p.ticker}`} size="sm" variant="outline" onClick={() => closePos(p)} className="h-7 rounded-sm border-signal-red/40 text-signal-red hover:bg-signal-red/10 text-xs">Close</Button></div>
                </div>
              ))}
            </>
          )}
          <div className="border-b border-t border-border px-4 py-3 label-xs flex items-center justify-between"><span>Open &amp; Recent Orders</span><span className="mono text-[10px] text-signal-green/80 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-signal-green animate-pulse" /> auto-refreshing</span></div>
          {orders.length === 0 ? <Empty>No orders yet</Empty> : orders.slice(0, 15).map((o) => (
            <div key={o.id} className="grid grid-cols-[1fr_70px_70px_100px_90px] gap-2 items-center px-4 py-2.5 border-b border-border/50 text-sm">
              <span className="mono font-medium">{o.ticker}</span>
              <span className={`mono uppercase text-xs ${o.side === "buy" ? "pos" : "neg"}`}>{o.side}</span>
              <span className="mono text-right">{o.quantity}</span>
              <span className="mono text-right text-xs text-muted-foreground uppercase">{o.status}</span>
              {["new", "accepted", "pending_new", "partially_filled"].includes(o.status) ? (
                <Button data-testid={`cancel-order-${o.id}`} size="sm" variant="outline" onClick={() => cancel(o.id)} className="h-7 rounded-sm border-border text-xs">Cancel</Button>
              ) : <span />}
            </div>
          ))}
        </div>
        <OrderForm paper={status.paper} onPlaced={loadAll} />
      </div>

      <BrokerAnalytics />
    </div>
  );
}
